import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X } from 'lucide-react';
import axios from 'axios';
import { createSlug } from '../../lib/utils';
import { API_URLS, API_CONFIG } from '../../config/api';
import MediaDisplay from '../MediaDisplay';
import {
  Briefcase,
  Users,
  Shield,
  CheckCircle,
  TrendingUp,
  DollarSign,
  Clock,
  Star,
  ArrowRight,
  Target,
  MessageSquare,
  Award,
  Zap,
  Search,
  Filter,
  Heart,
  Eye,
  Bookmark,
  Phone,
  Mail,
  MapPin,
  ShoppingCart,
  Server,
  Bot,
  Settings,
  Play,
  Send
} from 'lucide-react';

const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    company_name: '',
    project_description: '',
    budget_range: '',
    timeline: '',
    additional_requirements: ''
  });
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [stats, setStats] = useState([]);
  const [processSteps, setProcessSteps] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [companySettings, setCompanySettings] = useState({
    phone: '+90 555 123 4567',
    email: 'info@website-al.com',
    address: 'İstanbul, Türkiye',
    whatsapp: '+905324404011'
  });
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Icon mapping
  const iconMap = {
    'Briefcase': Briefcase,
    'ShoppingCart': ShoppingCart,
    'Server': Server,
    'MessageSquare': MessageSquare,
    'Bot': Bot,
    'Zap': Zap,
    'ArrowRight': ArrowRight,
    'Star': Star,
    'TrendingUp': TrendingUp,
    'Shield': Shield,
    'Users': Users,
    'Clock': Clock,
    'CheckCircle': CheckCircle,
    'Search': Search,
    'Target': Target,
    'Play': Play,
    'Settings': Settings
  };

  useEffect(() => {
    const fetchService = async () => {
      try {
        // Önce tüm hizmetleri al
        const response = await axios.get(`${API_CONFIG.BASE_URL}/backend/api.php?action=getServices`);
        const services = response.data.data || response.data;

        // Slug'a göre hizmet bul
        const foundService = services.find(s => s.slug === serviceSlug || createSlug(s.title || s.name || '') === serviceSlug);

        if (foundService) {
          setService(foundService);

          // Hizmet stats'larını al
          try {
            const statsResponse = await axios.get(`${API_URLS.SERVICE_STATS}&service_id=${foundService.id}`);
            setStats(statsResponse.data);
          } catch (statsErr) {
            console.error('Error fetching stats:', statsErr);
            setStats([]); // Boş array kullan, hardcoded data kullanma
          }

          // Hizmet process steps'lerini al
          try {
            const processResponse = await axios.get(`${API_URLS.SERVICE_PROCESS}&service_id=${foundService.id}`);
            setProcessSteps(processResponse.data);
          } catch (processErr) {
            console.error('Error fetching process steps:', processErr);
            setProcessSteps([]); // Boş array kullan, hardcoded data kullanma
          }

          // Hizmet testimonials'larını al
          try {
            const testimonialsResponse = await axios.get(`${API_CONFIG.BASE_URL}/backend/api.php?action=getServiceTestimonials&service_id=${foundService.id}`);
            if (testimonialsResponse.data && Array.isArray(testimonialsResponse.data)) {
              setTestimonials(testimonialsResponse.data);
            } else if (testimonialsResponse.data.data && Array.isArray(testimonialsResponse.data.data)) {
              setTestimonials(testimonialsResponse.data.data);
            } else {
              setTestimonials([]);
            }
          } catch (testimonialsErr) {
            console.error('Error fetching testimonials:', testimonialsErr);
            setTestimonials([]); // Boş array kullan
          }
        } else {
          throw new Error('Service not found');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching service:', err);
        setLoading(false);
        setService(null);
      }
    };

    const fetchCompanySettings = async () => {
      try {
        const response = await axios.get(`${API_CONFIG.BASE_URL}/backend/admin_api.php?action=getCompanySettings`);
        if (response.data.status === 'success' && response.data.data) {
          setCompanySettings({
            phone: response.data.data.phone || '+90 555 123 4567',
            email: response.data.data.email || 'info@website-al.com',
            address: response.data.data.address || 'İstanbul, Türkiye',
            whatsapp: response.data.data.whatsapp || '+905324404011'
          });
        }
      } catch (err) {
        console.error('Error fetching company settings:', err);
      }
    };

    fetchService();
    fetchCompanySettings();
  }, [serviceSlug]);

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setSubmittingQuote(true);

    try {
      const quoteData = {
        service_id: service.id,
        service_title: service.title,
        ...quoteFormData
      };

      await axios.post(API_URLS.QUOTE_REQUESTS, quoteData);
      setQuoteSubmitted(true);
      setShowQuoteForm(false);

      // Reset form
      setQuoteFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        company_name: '',
        project_description: '',
        budget_range: '',
        timeline: '',
        additional_requirements: ''
      });

      setTimeout(() => setQuoteSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alert('Teklif gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmittingQuote(false);
    }
  };




  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Hizmet yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Hizmet Bulunamadı</h1>
          <Link to="/">
            <Button>Ana Sayfaya Dön</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-20"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Sol Taraf - Yazılar */}
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur px-6 py-3 rounded-full text-lg font-medium">
                {(() => {
                  const iconName = service.icon;
                  const IconComponent = (typeof iconName === 'string' && iconMap[iconName]) ? iconMap[iconName] : Briefcase;
                  return IconComponent ? <IconComponent className="w-6 h-6 text-yellow-400" /> : null;
                })()}
                <span>{service.title}</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">
                <span className="bg-gradient-to-r from-yellow-400 via-blue-200 to-cyan-400 bg-clip-text text-transparent">
                  {service.title}
                </span>
              </h1>

              <p className="text-lg md:text-xl text-blue-100 leading-relaxed">
                {service.description}
              </p>
            </div>

            {/* Sağ Taraf - Resim/Video */}
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl overflow-hidden">
                <MediaDisplay
                  src={service?.image_url || "https://placehold.co/600x600/e0e7ff/6366f1?text=Hizmet+Görseli"}
                  alt={service.title}
                  className="w-full h-full object-cover"
                  videoProps={{ controls: true }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section 
        className="py-20 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Hizmet Detayları</h2>
                <p className="text-lg text-gray-600 mb-8">
                  {service.detailed_description || 'Bu hizmet hakkında detaylı bilgi için bizimle iletişime geçin. Profesyonel ekibimiz size en uygun çözümü sunacaktır.'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {service.features && service.features.length > 0 && service.features.map((feature, index) => (
                    <div key={`feature-${index}`} className="flex items-center space-x-3">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Çalışma Süreci</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {processSteps && processSteps.length > 0 ? (
                    processSteps.map((step, index) => {
                      const IconComponent = iconMap[step.icon] || MessageSquare;
                      return (
                        <div key={step.id || `step-${index}`} className="text-center">
                          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <IconComponent className="w-8 h-8 text-blue-600" />
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-2">{step.step_title || step.title}</h4>
                          <p className="text-gray-600 text-sm">{step.step_description || step.description}</p>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-3 text-center">
                      <p className="text-gray-500 text-lg">Bu hizmet için henüz çalışma süreci tanımlanmamış.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-[6.5rem]">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900">{service.title}</CardTitle>
                  {service.badge && (
                    <Badge className="bg-green-500 text-white w-fit">{service.badge}</Badge>
                  )}
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        const message = encodeURIComponent(`Merhaba! ${service.title} hizmeti hakkında bilgi almak istiyorum.`);
                        window.open(`https://wa.me/${companySettings.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
                      }}
                    >
                      <Send className="w-5 h-5 mr-2" />
                      WhatsApp İletişim
                    </Button>
                  </div>

                  <div className="border-t pt-6">
                    <h4 className="font-semibold text-gray-900 mb-4">İletişim Bilgileri</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Phone className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <a href={`tel:${companySettings.phone}`} className="text-gray-700 hover:text-blue-600">
                          {companySettings.phone}
                        </a>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Mail className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        <a href={`mailto:${companySettings.email}`} className="text-gray-700 hover:text-blue-600">
                          {companySettings.email}
                        </a>
                      </div>
                      <div className="flex items-start space-x-3">
                        <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{companySettings.address}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Only show if testimonials exist */}
      {testimonials && testimonials.length > 0 && (
        <section 
          className="py-20 bg-gray-50"
          style={{ 
            opacity: contentReady ? 1 : 0, 
            transition: 'opacity 0.6s ease-in' 
          }}
        >
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Müşteri Yorumları</h2>
              <p className="text-xl text-gray-600">
                Bu hizmeti kullanan müşterilerimizin deneyimleri
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <Card key={testimonial.id || `testimonial-${index}`} className="bg-white">
                  <CardContent className="p-8">
                    <div className="flex items-center mb-4">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>

                    <p className="text-lg mb-6 italic text-gray-700">"{testimonial.content || testimonial.comment}"</p>

                    <div className="border-t pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-bold text-gray-900">{testimonial.name || testimonial.customer_name}</h4>
                          <p className="text-gray-600">{testimonial.role || testimonial.company}</p>
                        </div>
                        {testimonial.project && (
                          <div className="text-right">
                            <Badge className="bg-blue-500 text-white mb-2">
                              {testimonial.project}
                            </Badge>
                            {testimonial.budget && (
                              <p className="text-sm text-gray-600">Bütçe: {testimonial.budget}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}



      {/* CTA Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Projenizi Bugün Başlatın
          </h2>
          <p className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto">
            Uzman ekibimizle birlikte hayalinizdeki projeyi gerçekleştirelim. Size özel çözümler üretiyor, projelerinizi başarıyla tamamlıyoruz.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link to="/iletisim">
              <Button
                size="lg"
                className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-12 py-4 text-xl"
              >
                <MessageSquare className="w-6 h-6 mr-3" />
                İletişim
              </Button>
            </Link>

            <Button
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-12 py-4 text-xl"
              onClick={() => {
                const message = encodeURIComponent(`Merhaba! ${service.title} hizmeti hakkında bilgi almak istiyorum.`);
                window.open(`https://wa.me/${companySettings.whatsapp.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
              }}
            >
              <Send className="w-6 h-6 mr-3" />
              WhatsApp İletişim
            </Button>
          </div>
        </div>
      </section>

      {/* Quote Form Modal */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Teklif Formu - {service?.title}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowQuoteForm(false)}
                  className="hover:bg-gray-100"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleQuoteSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name">Ad Soyad *</Label>
                    <Input
                      id="customer_name"
                      value={quoteFormData.customer_name}
                      onChange={(e) => setQuoteFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                      required
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>

                  <div>
                    <Label htmlFor="customer_email">E-posta *</Label>
                    <Input
                      id="customer_email"
                      type="email"
                      value={quoteFormData.customer_email}
                      onChange={(e) => setQuoteFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                      required
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_phone">Telefon</Label>
                    <Input
                      id="customer_phone"
                      value={quoteFormData.customer_phone}
                      onChange={(e) => setQuoteFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                      placeholder="+90 555 123 4567"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company_name">Şirket Adı</Label>
                    <Input
                      id="company_name"
                      value={quoteFormData.company_name}
                      onChange={(e) => setQuoteFormData(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Şirket adınız (varsa)"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="project_description">Proje Açıklaması *</Label>
                  <Textarea
                    id="project_description"
                    value={quoteFormData.project_description}
                    onChange={(e) => setQuoteFormData(prev => ({ ...prev, project_description: e.target.value }))}
                    rows={4}
                    required
                    placeholder="Projenizin detaylarını açıklayın. Ne yapmak istiyorsunuz? Hangi özellikler gerekli?"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="budget_range">Bütçe Aralığı</Label>
                    <Select
                      value={quoteFormData.budget_range}
                      onValueChange={(value) => setQuoteFormData(prev => ({ ...prev, budget_range: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Bütçe seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="₺1,000 - ₺5,000">₺1,000 - ₺5,000</SelectItem>
                        <SelectItem value="₺5,000 - ₺15,000">₺5,000 - ₺15,000</SelectItem>
                        <SelectItem value="₺15,000 - ₺50,000">₺15,000 - ₺50,000</SelectItem>
                        <SelectItem value="₺50,000+">₺50,000+</SelectItem>
                        <SelectItem value="Belirtilmemiş">Belirtilmemiş</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="timeline">Zaman Çizelgesi</Label>
                    <Select
                      value={quoteFormData.timeline}
                      onValueChange={(value) => setQuoteFormData(prev => ({ ...prev, timeline: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Süre seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-2 hafta">1-2 hafta</SelectItem>
                        <SelectItem value="1 ay">1 ay</SelectItem>
                        <SelectItem value="2-3 ay">2-3 ay</SelectItem>
                        <SelectItem value="3+ ay">3+ ay</SelectItem>
                        <SelectItem value="Belirtilmemiş">Belirtilmemiş</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="additional_requirements">Ek Gereksinimler</Label>
                  <Textarea
                    id="additional_requirements"
                    value={quoteFormData.additional_requirements}
                    onChange={(e) => setQuoteFormData(prev => ({ ...prev, additional_requirements: e.target.value }))}
                    rows={3}
                    placeholder="Ek gereksinimler, özel istekler veya notlarınız varsa buraya yazabilirsiniz."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={submittingQuote}
                  >
                    {submittingQuote ? 'Gönderiliyor...' : 'Teklif Gönder'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowQuoteForm(false)}
                    disabled={submittingQuote}
                  >
                    İptal
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Success Message */}
      {quoteSubmitted && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 flex items-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>Teklif başarıyla gönderildi! En kısa sürede size dönüş yapacağız.</span>
        </div>
      )}
    </div>
  );
};

export default ServiceDetailPage;
